import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { WorkshopJobService } from '../../shared/services/workshop-job.service';
import { WorkshopJobListItem } from '../../models/workshop-job-list-item';
import { WorkshopJobWorkStatus } from '../../models/workshop-job';

interface WorkshopJobStatusOptions {
  value: statusKey;
  jobCount: number;
}

const statusKeys: statusKey[] = [
  'booked_in',
  'assessed',
  'quotation',
  'quote_approved',
  'waiting_for_parts',
  'repair_started',
  'completed',
  'cancelled'
];

export type statusKey =
  | 'all'
  | 'in_progress'
  | 'closed'
  | WorkshopJobWorkStatus;

interface WorkshopJobListStoreState {
  jobs: WorkshopJobListItem[];
  loading: boolean;
  filter: {
    search: string;
    status: statusKey;
    customer: 'external' | 'internal' | 'both';
  };
  availableStatuses: {
    all?: number;
    booked_in?: number;
    assessed?: number;
    quotation?: number;
    waiting_for_parts?: number;
    repair_started?: number;
    completed?: number;
    cancelled?: number;
  };
}

const initialState: WorkshopJobListStoreState = {
  jobs: [],
  loading: false,
  filter: {
    search: '',
    status: 'in_progress',
    customer: 'both'
  },
  availableStatuses: {}
};

@Injectable()
export class WorkshopJobListStoreService extends ComponentStore<WorkshopJobListStoreState> {
  constructor(private workshopJobService: WorkshopJobService) {
    super(initialState);
  }

  readonly loading$ = this.select((state) => state.loading);
  readonly filter$ = this.select((state) => state.filter);
  readonly filterQuery$ = this.select(this.filter$, (filter) => {
    const { search, status, customer } = filter;
    if (status === 'all') {
      return { ...filter, status: statusKeys };
    }
    if (status === 'in_progress') {
      return {
        ...filter,
        status: [
          'booked_in',
          'assessed',
          'quotation',
          'quote_approved',
          'waiting_for_parts',
          'repair_started'
        ]
      };
    }
    if (status === 'closed') {
      return { ...filter, status: ['completed', 'cancelled'] };
    }
    return {
      ...filter,
      status: [status]
    };
  });
  readonly availableStatuses$ = this.select((state) =>
    Object.entries(state.availableStatuses).map(([key, value]) => ({
      value: key,
      jobCount: value as number
    }))
  );

  readonly setFilter = this.updater(
    (state, filter: Partial<WorkshopJobListStoreState['filter']>) => ({
      ...state,
      filter: { ...state.filter, ...filter }
    })
  );

  readonly setStatusCounts = this.updater(
    (state, statusCounts: { [key: string]: number }) => {
      const emptyAggregates = {
        all: 0,
        in_progress: 0,
        closed: 0
      };
      const allZeroCount = statusKeys.reduce<{ [key: string]: number }>(
        (acc, status) => {
          acc[status] = 0;
          return acc;
        },
        emptyAggregates
      );
      const availableStatuses = Object.entries(statusCounts).reduce(
        (acc, [key, value]) => {
          acc[key] = value;
          acc['all'] = acc['all'] + value;
          if (key !== 'cancelled' && key !== 'completed') {
            acc['in_progress'] = acc['in_progress'] + value;
          } else {
            acc['closed'] = acc['closed'] + value;
          }
          return acc;
        },
        allZeroCount
      );
      return { ...state, availableStatuses };
    }
  );
}
